import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEdit, FaPlus, FaSpinner, FaLanguage } from 'react-icons/fa';
import { getAllLanguages } from '../components/utils/languageApi';

const Languages = () => {
  const navigate = useNavigate();
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all languages from the collection
        const languages = await getAllLanguages();
        setLanguages(languages);

      } catch (error) {
        console.error('Error fetching languages:', error);
        setError('Failed to fetch languages');
      } finally {
        setLoading(false);
      }
    };

    fetchLanguages();
  }, []);

  const handleEditLanguage = (code) => {
    navigate(`/langjson/${code}`);
  };

  const handleAddLanguage = () => {
    // This could be expanded to add a new language
    // For now, we'll just show a message
    alert('Adding new languages should be done through the PocketBase admin panel.');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <FaSpinner className="animate-spin text-4xl text-[#A1E744] mx-auto mb-4" />
          <p className="text-gray-600">Loading languages...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="text-6xl text-red-400 mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Languages</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-[#A1E744] text-white px-6 py-3 rounded-lg hover:bg-[#8BCF3A] transition-colors duration-200"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center">
            <FaLanguage className="mr-3 text-[#A1E744]" />
            Languages
          </h1>
          <p className="text-gray-600 mt-2">
            Manage language JSON templates for your application
          </p>
        </div>
        
        <button
          onClick={handleAddLanguage}
          className="bg-[#A1E744] text-white px-6 py-3 rounded-lg hover:bg-[#8BCF3A] transition-colors duration-200 flex items-center"
        >
          <FaPlus className="mr-2" />
          Add Language
        </button>
      </div>

      {/* Languages Grid */}
      {languages.length === 0 ? (
        <div className="text-center py-12">
          <FaLanguage className="text-6xl text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-600 mb-2">No Languages Found</h3>
          <p className="text-gray-500">
            No languages have been configured yet. Please add languages through the PocketBase admin panel.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {languages.map((language) => (
            <div
              key={language.id}
              className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow duration-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-[#A1E744] rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {language.code?.toUpperCase().charAt(0)}
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-semibold text-gray-800">
                      {language.name || language.code}
                    </h3>
                    <p className="text-sm text-gray-500 font-mono">
                      {language.code}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  {language.description || 'No description available'}
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  {language.json_template ? 
                    `${Object.keys(JSON.parse(language.json_template || '{}')).length} keys` : 
                    'No template'
                  }
                </div>
                
                <button
                  onClick={() => handleEditLanguage(language.code)}
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center text-sm"
                >
                  <FaEdit className="mr-2" />
                  Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Languages; 